import React, { useState, useEffect } from 'react';
import { trackTikTokEvent, initTikTokPixel } from '../providers/tiktokPixel';
import { trackSnapchatEvent, initSnapchatPixel } from '../providers/snapchatPixel';
import { getTranslations } from '../translations';
import { GEOS, getCurrentGeo } from '../geoConfig';

const Final = () => {
    const currentGeoId = getCurrentGeo();
    const geo = GEOS[currentGeoId] || GEOS.qa;

    const [lang] = useState(() => {
        const urlLang = new URLSearchParams(window.location.search).get('lang');
        if (urlLang && geo.supportedLangs.includes(urlLang)) return urlLang;
        const savedLang = localStorage.getItem('dino_lang');
        if (savedLang && geo.supportedLangs.includes(savedLang)) return savedLang;
        return geo.defaultLang;
    });

    const t = getTranslations(currentGeoId, lang);

    useEffect(() => {
        document.documentElement.lang = lang;
        document.documentElement.dir = t.dir;

        // TikTok Pixel Init & CompletePayment on confirmation
        let tt = new URLSearchParams(window.location.search).get('tt');
        if (tt) {
            initTikTokPixel(tt);
        } else {
            initTikTokPixel();
        }
        trackTikTokEvent('CompletePayment', {
            content_id: localStorage.getItem('tierName') || 'VIP Ticket',
            value: parseFloat(localStorage.getItem('summ')) || 127.50,
            currency: localStorage.getItem('currency') || geo.currencyCode
        });

        // Snapchat Pixel Init & PURCHASE on confirmation
        let sc = new URLSearchParams(window.location.search).get('sc');
        if (sc) {
            initSnapchatPixel(sc);
        } else {
            initSnapchatPixel();
        }
        trackSnapchatEvent('PURCHASE', {
            item_ids: [localStorage.getItem('tierName') || 'VIP Ticket'],
            price: parseFloat(localStorage.getItem('summ')) || 35.00,
            currency: localStorage.getItem('currency') || geo.currencyCode,
            transaction_id: localStorage.getItem('reservedSeat') || ('ticket-' + Date.now())
        });
    }, [lang, t.dir, geo.currencyCode]);

    const seatCode = localStorage.getItem('reservedSeat') || (geo.seatPrefix + '-2026');
    const userEmail = localStorage.getItem('userEmail') || (lang === 'ar' ? 'بريدك الإلكتروني' : 'your.email@domain.com');
    const tierName = localStorage.getItem('tierName') || (lang === 'ar' ? 'تذكرة كبار الشخصيات VIP الشاملة' : (lang === 'zh' ? '全通行 VIP 貴賓門票' : 'Universal VIP Pass'));
    const selectedDate = localStorage.getItem('selectedDate') || t.openPassValidity;
    const timeSlot = localStorage.getItem('timeSlot') || t.openPassHours;

    const formatDateLabel = (dateStr) => {
        if (t.dates && t.dates[dateStr]) {
            return `${t.dates[dateStr].day}، ${t.dates[dateStr].dateStr}`;
        }
        return dateStr;
    };

    return (
        <div className={`min-h-screen bg-[#0a130e] text-[#f5f1e8] flex items-center justify-center font-['Cairo',sans-serif] p-4 selection:bg-[#fbc61c] selection:text-black ${lang === 'en' ? 'font-sans' : ''}`} dir={t.dir}>
            <div className="max-w-lg w-full bg-gradient-to-b from-[#0f2418] via-[#0d1d13] to-[#08110d] rounded-3xl shadow-2xl border border-[#fbc61c]/30 p-8 md:p-10 text-center relative overflow-hidden">
                
                {/* Glow Background */}
                <div className="absolute top-0 right-1/2 translate-x-1/2 w-48 h-48 bg-emerald-500/10 rounded-full filter blur-3xl pointer-events-none"></div>

                <div className="w-20 h-20 bg-emerald-500/20 border border-emerald-400/40 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-900/40">
                    <svg className="w-10 h-10 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
                </div>
                
                <div className="inline-block bg-[#fbc61c]/20 border border-[#fbc61c]/40 text-[#fbc61c] px-3.5 py-1 rounded-full text-xs font-bold mb-3">
                    {t.final?.badge || (lang === 'ar' ? 'تم تأكيد الحجز بنجاح' : 'Booking Confirmed')}
                </div>

                <h2 className="text-2xl md:text-3xl font-black text-white mb-3">
                    {t.final?.confirmedTitle || 'Booking Confirmed'}
                </h2>
                <p className="text-slate-300 text-sm leading-relaxed mb-6">
                    {t.final?.confirmedSubtitle || 'Confirmation sent to:'} <br/>
                    <span className="font-bold text-[#fbc61c] font-mono">{userEmail}</span>
                </p>

                {/* Ticket Details Card */}
                <div className="bg-black/40 rounded-2xl p-5 mb-8 text-right border border-white/10 space-y-3 backdrop-blur-md">
                    <div className="flex justify-between items-center pb-2 border-b border-white/10">
                        <span className="text-xs font-bold text-slate-400">{t.final?.eventLabel || 'Event:'}</span>
                        <span className="font-bold text-sm text-white">{t.headerTitle}</span>
                    </div>

                    <div className="flex justify-between items-center pb-2 border-b border-white/10">
                        <span className="text-xs font-bold text-slate-400">{t.final?.venueLabel || 'Venue:'}</span>
                        <span className="font-semibold text-xs text-slate-200">{t.venueName}</span>
                    </div>

                    <div className="flex justify-between items-center pb-2 border-b border-white/10">
                        <span className="text-xs font-bold text-slate-400">{t.final?.sessionLabel || 'Session:'}</span>
                        <span className="font-bold text-xs text-[#fbc61c]">{formatDateLabel(selectedDate)} ({timeSlot})</span>
                    </div>

                    <div className="flex justify-between items-center pb-2 border-b border-white/10">
                        <span className="text-xs font-bold text-slate-400">{t.final?.bookingRefLabel || 'Reference:'}</span>
                        <span className="font-bold text-sm font-mono text-[#fbc61c] bg-[#fbc61c]/10 px-2.5 py-0.5 rounded-lg">{seatCode}</span>
                    </div>

                    <div className="flex justify-between items-center">
                        <span className="text-xs font-bold text-slate-400">{t.final?.packageLabel || 'Package:'}</span>
                        <span className="font-bold text-xs text-emerald-400">{tierName}</span>
                    </div>
                </div>

                <a 
                    href={`index.html?lang=${lang}`} 
                    className="inline-flex items-center justify-center gap-2 w-full dw-btn-primary py-3.5 px-6 rounded-2xl font-bold text-sm shadow-lg cursor-pointer"
                >
                    <svg className="w-4 h-4 rtl:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
                    <span>{t.final?.backHomeBtn || 'Back to Event Homepage'}</span>
                </a>
            </div>
        </div>
    );
};

export default Final;
